<div class="font-lato border-b border-gray-100 px-5 py-3 text-sm text-black">
  Bill statements for the past 24 months are available for download.
</div>

@if (isInvoicesLoading()) {
  <ul class="animate-pulse" role="status" aria-label="Loading invoices">
    @for (row of [1, 2, 3, 4, 5, 6]; track row) {
      <li class="flex items-center border-b border-gray-100" aria-hidden="true">
        <div
          class="flex w-16 shrink-0 items-center justify-center border-r border-gray-100 py-5"
        >
          <div class="h-7 w-7 rounded bg-gray-200"></div>
        </div>
        <div class="flex flex-1 items-center px-5">
          <div class="h-4 w-40 rounded bg-gray-200"></div>
        </div>
        <div class="flex items-center gap-2 pr-5">
          <div class="h-4 w-24 rounded bg-gray-200"></div>
          <div class="h-4 w-20 rounded bg-gray-200"></div>
        </div>
        <div class="flex items-center gap-2 pr-4">
          <div class="h-8 w-8 rounded bg-gray-200"></div>
          <div class="h-8 w-8 rounded bg-gray-200"></div>
        </div>
      </li>
    }
  </ul>
}

@if (!invoices().length) {
  <div
    role="status"
    aria-live="polite"
    class="flex flex-col items-center justify-center gap-4 px-4 py-10"
  >
    <mat-icon
      fontIcon="description"
      class="text-gray-300"
      [style.font-size.px]="48"
      [style.width.px]="48"
      [style.height.px]="48"
      aria-hidden="true"
    ></mat-icon>
    <div class="text-center">
      <h3 class="font-montserrat mb-1 text-base font-semibold text-black">
        No invoices yet
      </h3>
      <p class="font-lato text-sm text-gray-600">
        Invoices will appear here as they become available. Bill statements from
        the past 24 months are available for download once generated.
      </p>
    </div>
  </div>
}

<ul
  aria-label="Invoices"
  [attr.aria-hidden]="invoices()?.length === 0 ? 'true' : null"
>
  @for (invoice of invoices(); track invoice.Id) {
    <li
      class="hover:bg-blue-75 flex items-center border-b border-gray-100 py-1 transition-colors last:border-b-0"
      [class.bg-blue-75]="selectedInvoiceId() === invoice.Id"
      [attr.aria-current]="selectedInvoiceId() === invoice.Id ? 'true' : null"
      [attr.aria-selected]="selectedInvoiceId() === invoice.Id"
    >
      <button
        type="button"
        class="hidden w-16 shrink-0 items-center justify-center border-r border-gray-100 py-4 text-blue-700 transition hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset lg:visible lg:flex"
        [attr.aria-disabled]="!invoice.file ? 'true' : null"
        [disabled]="downloadingIds().has(invoice.Id) || null"
        [attr.aria-busy]="downloadingIds().has(invoice.Id) ? 'true' : null"
        [attr.aria-label]="
          !invoice.file
            ? 'Invoice PDF not yet available'
            : downloadingIds().has(invoice.Id)
              ? 'Downloading PDF, please wait'
              : 'Download invoice PDF'
        "
        [matTooltip]="
          !invoice.file ? 'PDF not yet available' : 'Download invoice PDF'
        "
        matTooltipPosition="below"
        [ngClass]="{
          'cursor-pointer': !!invoice.file && !downloadingIds().has(invoice.Id),
          'cursor-not-allowed opacity-40': !invoice.file,
          'cursor-wait opacity-60': downloadingIds().has(invoice.Id),
        }"
        (click)="onDownload(invoice)"
      >
        @if (downloadingIds().has(invoice.Id)) {
          <mat-spinner [diameter]="20" aria-hidden="true"></mat-spinner>
        } @else {
          <mat-icon
            svgIcon="pdf-file"
            class="h-7! w-7!"
            aria-hidden="true"
          ></mat-icon>
        }
      </button>

      <span class="font-lato flex flex-1 items-center px-5 text-sm text-black">
        <time [attr.datetime]="invoice.Transaction_Date__c">
          {{ invoice.Transaction_Date__c | date: 'MMMM d, yyyy' }}
        </time>
      </span>

      <div class="flex items-center gap-5 px-6">
        @if (invoice.Billing_Status__c) {
          <span
            class="font-lato hidden rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide uppercase lg:flex"
            [ngClass]="getStatusClasses(invoice.Billing_Status__c)"
            [attr.aria-label]="'Status: ' + invoice.Billing_Status__c"
          >
            {{ invoice.Billing_Status__c }}
          </span>
        }
        @if (invoice.Transaction_Amount__c != null) {
          <span
            class="font-montserrat text-sm font-semibold text-black tabular-nums"
          >
            {{
              invoice.Transaction_Amount__c
                | currency: 'USD' : 'symbol' : '1.2-2'
            }}
          </span>
        }
      </div>

      <div
        class="flex items-center gap-2 pr-5 pl-2"
        role="group"
        [attr.aria-label]="
          'Actions for invoice dated ' +
          (invoice.Transaction_Date__c | date: 'MMMM d, yyyy')
        "
      >
        <!-- Download: aria-disabled keeps it focusable so tooltip/keyboard can reach it -->
        <button
          type="button"
          data-testid="download-invoice-button"
          [attr.data-invoice-id]="invoice.Id"
          [attr.aria-disabled]="!invoice.file ? 'true' : null"
          [disabled]="downloadingIds().has(invoice.Id) || null"
          [attr.aria-busy]="downloadingIds().has(invoice.Id) ? 'true' : null"
          [attr.aria-label]="
            !invoice.file
              ? 'Invoice PDF not yet available'
              : downloadingIds().has(invoice.Id)
                ? 'Downloading PDF, please wait'
                : 'Download invoice PDF'
          "
          [matTooltip]="
            !invoice.file ? 'PDF not yet available' : 'Download invoice PDF'
          "
          matTooltipPosition="above"
          class="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-400 text-black transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
          [ngClass]="{
            'cursor-pointer hover:border-blue-700 hover:bg-blue-100 hover:text-blue-700':
              !!invoice.file && !downloadingIds().has(invoice.Id),
            'cursor-not-allowed opacity-40': !invoice.file,
            'cursor-wait opacity-60': downloadingIds().has(invoice.Id),
          }"
          (click)="onDownload(invoice)"
        >
          @if (downloadingIds().has(invoice.Id)) {
            <mat-spinner [diameter]="18" aria-hidden="true"></mat-spinner>
          } @else {
            <mat-icon
              fontIcon="download"
              class="flex! h-4! w-4! items-center! justify-center! text-[16px]!"
              aria-hidden="true"
            ></mat-icon>
          }
        </button>

        <!-- View: same aria-disabled pattern -->
        <button
          type="button"
          data-testid="view-invoice-button"
          [attr.data-invoice-id]="invoice.Id"
          [attr.aria-disabled]="!invoice.file ? 'true' : null"
          [disabled]="viewingIds().has(invoice.Id) || null"
          [attr.aria-busy]="viewingIds().has(invoice.Id) ? 'true' : null"
          [attr.aria-label]="
            !invoice.file
              ? 'Invoice PDF not yet available'
              : viewingIds().has(invoice.Id)
                ? 'Opening PDF, please wait'
                : 'View invoice PDF'
          "
          [matTooltip]="
            !invoice.file ? 'PDF not yet available' : 'View invoice PDF'
          "
          matTooltipPosition="above"
          class="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-400 text-black transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
          [ngClass]="{
            'cursor-pointer hover:border-blue-700 hover:bg-blue-100 hover:text-blue-700':
              !!invoice.file && !viewingIds().has(invoice.Id),
            'cursor-not-allowed opacity-40': !invoice.file,
            'cursor-wait opacity-60': viewingIds().has(invoice.Id),
          }"
          (click)="onView(invoice)"
        >
          @if (viewingIds().has(invoice.Id)) {
            <mat-spinner [diameter]="18" aria-hidden="true"></mat-spinner>
          } @else {
            <mat-icon
              fontIcon="visibility"
              class="flex! h-4! w-4! items-center! justify-center! text-[16px]!"
              aria-hidden="true"
            ></mat-icon>
          }
        </button>
      </div>
    </li>
  }
</ul>
